import Settings from '@client/models/store/settings';
import {FeatureId} from '@client/constants/features';
// eslint-disable-next-line import/no-cycle
import platformSettingsService from '@client/services/platformSettings';
import {Logger, Category} from '@simpleinvest/front-end-logger';
import {getUserId} from '@simpleinvest/front-end-auth';
// eslint-disable-next-line import/no-cycle
import settingsService from '@client/services/settings';
import fromS3 from '@client/util/s3';
import {ErrorRouteNames} from '@client/routes/errors';

const logger = new Logger({
  category: Category.PrivateLabel,
  fileName: 'settings.js',
});

const setFeatureData = ({dispatch, commit, state}, result) => {
  state.enterprise.features.forEach((feature) => {
    switch (feature.toLowerCase()) {
      case 'chat': {
        dispatch('chat/getChatSettings', result.chatSettings, {root: true});
        break;
      }
      case 'footermenu': {
        commit('SET_ENTERPRISE_CLIENT_FOOTER_MENU', result.footerInfo);
        break;
      }
      case 'sso': {
        commit('SET_ENTERPRISE_SSO_SETTINGS', result.ssoSettings);
        break;
      }
      case 'riskgauge': {
        commit('SET_ENTERPRISE_GAUGE_TYPE', result.riskGaugeType);
        break;
      }
      default: {
        logger.addBreadcrumb({
          callingFuncName: 'setFeatureData',
          message: `${feature} feature not implemented`,
        });

        break;
      }
    }
  });

  commit('SET_ENTERPRISE_CLIENT_HEADER_MENU', result.headerInfo);
};

const stateObj = function () {
  return new Settings();
};

const gettersObj = {
  ria: (state) => state.ria,
  riaEmailAddress: (state) => state.ria?.emailAddress,
  riaPhoneNumber: (state) => state.ria?.phoneNumber,
  riaDisclaimer: (state) => {
    const rawDisclaimer = state.ria?.disclaimer;

    if (rawDisclaimer) {
      const SCRIPT_REGEX = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
      let sanitizedDisclaimer = rawDisclaimer;

      while (SCRIPT_REGEX.test(sanitizedDisclaimer)) {
        sanitizedDisclaimer = sanitizedDisclaimer.replace(SCRIPT_REGEX, ' ');
      }

      return `${sanitizedDisclaimer}`;
    }

    return '';
  },
  riaWebsite: (state) => state.ria.website,
  riaContactPage: (state) => state.ria.contactPage,
  riaAdditionalDisclaimer: (state) => state.ria.additionalDisclaimer,
  riaCompanyLegalName: (state) => state.ria.companyLegalName,
  areRiaSettingsLoaded: (state) => state.areRiaSettingsLoaded,
  riaLoginLogoLink: (state) => {
    const {website} = state.ria;

    return website === 'none' || website === 'None' || website === '' ? '' : website;
  },
  riaLoginLogoPath: (state) => (state.ria.squareLogo ? `${process.env.VUE_APP_BUCKET_URL}${state.ria.squareLogo}` : null),
  riaLoginVideoPath: (state, getters) => {
    if (!getters.riaLoginHasBackgroundVideo || !state.ria.loginBackground) {
      return '';
    }

    return fromS3(state.ria.loginBackground);
  },
  riaLoginBackgroundStyle: (state) =>
    state.ria.loginBackground
      ? {
          'background-image': `url('${process.env.VUE_APP_BUCKET_URL}${state.ria.loginBackground}')`,
          'background-size': `cover`,
        }
      : {},
  riaLoginHasBackgroundVideo: (state) => {
    if (state.ria?.loginBackground) {
      return state.ria.loginBackground.includes('.mp4');
    }

    return false;
  },
  isEnterpriseFooter: (state) => state.footer?.menu?.length > 0,
  enterpriseFooterMenuItems: (state) => state.footer.menu,
  canOpenAccount: (state) => state.account.canOpen,
  riaName: (state) => state.riaName,
  showAggregation: (state) => state.ria.showAggregation,
  showTotalCash: (state) => state.showTotalCash,
  isClassicMode: (state) => {
    const enterpriseFeatures = state.enterprise.features;

    return enterpriseFeatures.includes('ClassicMode');
  },
  showWealthProjectionForAccount: (state) => state.account.showWealthProjection,
  showWealthProjectionForGoal: (state) => state.goal.showWealthProjection,
  showCurrentPortfolioForGoal: (state) => state.goal.showCurrentPortfolio,
  canAddGoal: (state) => state.goal.canBeModified,
  canAttachGoalAccount: (state) => state.goal.canAttachAccount,
  showTargetPortfolioForGoal: (state) => state.goal.showTargetPortfolio,
  showCurrentPortfolioForAccount: (state) => state.account.showCurrentPortfolio,
  showTargetPortfolioForAccount: (state) => state.account.showTargetPortfolio,
  showTotalCashForAccount: (state) => state.account.showTotalCash,
  canTakeRiskQuestionnaire: (state) => state.canTakeRiskQuestionnaire,
  availableAccountTypes: (state) => state.account.availableAccountTypes,
  canLiquidateAccounts: (state) => state.ria.canLiquidateAccounts,
  canMoveMoney: (state) => state.canMoveMoney,
  enterprise: (state) => state.enterprise,
  requiresBankLinkIdentityVerification: (state) => state.requiresBankLinkIdentityVerification,
  accountMinimumAmount: (state) => state.accountMinimumAmount || 0,
  clientMinimumAmount: (state) => state.clientMinimumAmount || 0,
  requiresBalanceCheck: (state) => state.requiresBalanceCheck,
  showContactUs: (state) => state.showContactUs,
  onboardingSegment: ({segment}) => segment,
  bccOnLiquidationEmails: (state) => state.bccOnLiquidationEmails,
  wealthProjectionDisclaimer: (state) => state.ria.wealthProjectionDisclaimer,
  welcomeImage: (state) => state.ria.cdoWelcomeImage,
  congratulationImage: (state) => state.ria.congratulationImage,
};

const actions = {
  async getSegmentSettings({rootGetters, commit}) {
    const clUserId = rootGetters['cognitoUser/userId'];

    try {
      const platformSettings = await platformSettingsService.getPlatformSettings(clUserId);

      commit('SET_PLATFORM_SETTINGS', platformSettings);
    } catch (error) {
      logger.logException({error});
    }
  },
  async getClientOnboardingSegment({rootGetters, commit}, {segmentId}) {
    const {riaId} = rootGetters;

    try {
      const response = await platformSettingsService.getClientOnboardingSegment({segmentId, riaId});

      commit('SET_LINK_ALIAS', response);
    } catch (error) {
      logger.logException({error});
    }
  },
  async getCdoSettings({commit}) {
    const clUserId = await getUserId();
    const result = await settingsService.getCdoSettings({clUserId});

    commit('SET_CDO_SETTINGS', result);

    return result;
  },
  // eslint-disable-next-line require-await
  async setCanOverrideRisk({commit}, payload) {
    commit('SET_RISK_SETTINGS', payload);
  },

  async getAppSettings({dispatch, commit, state}, {url}) {
    try {
      const response = await settingsService.getAppSettings({url});
      const result = response?.data?.data;

      if (!result?.riaFeatures?.enterpriseId) {
        this.$router.push({name: ErrorRouteNames.NotFoundPage});
        throw new Error('enterpriseId is not set.');
      }

      commit('SET_APP_SETTINGS', result?.riaFeatures);
      commit('SET_ENTERPRISE_ID', result?.riaFeatures?.enterpriseId);
      commit('SET_ENTERPRISE_FEATURES', result?.enterpriseFeatures?.features);
      setFeatureData({dispatch, commit, state}, result.enterpriseFeatures);

      return result;
    } catch (error) {
      logger.logException({error});

      return false;
    }
  },
  setAppSettings({dispatch, commit}, appSettings) {
    commit('SET_ENTERPRISE_ID', appSettings.riaFeatures.riaInformation.enterpriseId);
    dispatch('chat/getChatSettings', appSettings.chatSettings, {root: true});
    commit('SET_APP_SETTINGS', appSettings?.riaFeatures);
  },
};

const mutations = {
  SET_LINK_ALIAS(state, payload) {
    state.linkAlias = payload.linkAlias;
  },
  SET_RISK_SETTINGS(state, payload) {
    state.canOverrideRisk = payload;
  },
  SET_ENTERPRISE_ID(state, payload) {
    state.enterprise.enterpriseId = payload;
  },
  SET_PLATFORM_SETTINGS(state, payload) {
    state.isSegmentsSet = true;

    state.goal.loadData(payload);
    state.account.loadData(payload);

    state.canMoveMoney = payload.find((setting) => setting.id === FeatureId.BANK_TRANSFER)?.value;
    state.showTotalCash = payload.find((setting) => setting.id === FeatureId.TOTAL_CASH_VALUE_BOX)?.value;
    state.canOverrideRisk = payload.find((setting) => setting.id === FeatureId.RISK_TOL_OVERRIDE)?.value;
    state.canTakeRiskQuestionnaire = payload.find((setting) => setting.id === FeatureId.RISK_TOL_QUEST)?.value;
    state.ria.showAggregation = payload.find((setting) => setting.id === FeatureId.ACCOUNT_AGGREGATION)?.value;
    state.ria.canLiquidateAccounts = payload.find((setting) => setting.id === FeatureId.FULL_LIQUIDATION)?.value;
    state.showContactUs = payload.find((setting) => setting.id === FeatureId.CONTACT_US)?.value;
    state.requiresBankLinkIdentityVerification = payload.find(
      (setting) => setting.id === FeatureId.BANK_LINK_IDENTITY_VERIFICATION
    )?.value;
    state.requiresBalanceCheck = payload.find((setting) => setting.id === FeatureId.BALANCE_CHECK)?.value;
    state.accountMinimumAmount = payload.find((setting) => setting.id === FeatureId.ACCOUNT_MINIMUM_AMOUNT)?.value;
    state.bccOnLiquidationEmails = payload.find((setting) => setting.id === FeatureId.BCC_LIQUIDATION_EMAILS)?.value;
    state.clientMinimumAmount = payload.find((setting) => setting.id === FeatureId.CLIENT_MINIMUM_AMOUNT)?.value;
  },
  SET_CDO_SETTINGS(state, payload) {
    state.cdo = payload;
  },

  SET_APP_SETTINGS(state, payload) {
    state.ria.setRiaImages(payload.riaImages);
    state.ria.setRiaDesignStyle(payload.riaDesignStyle);
    state.ria.setRiaLogo(payload.riaLogos);
    state.ria.setRiaText(payload.riaText);
    state.ria.setRiaInformation(payload.riaInformation);
    state.areRiaSettingsLoaded = true;
    state.enterprise.featuresLoaded = true;
  },
  SET_USER_CAN_ADD_ACCOUNT(state, payload) {
    state.user.canAddAccount = payload;
  },
  SET_USER_SETTINGS(state, payload) {
    state.user.loadData(payload);
  },
  SET_ENTERPRISE_FEATURES(state, payload) {
    state.enterprise.features = payload.map((row) => row.feature);
    state.enterprise.featuresLoaded = true;
  },
  SET_ENTERPRISE_CLIENT_FOOTER_MENU(state, payload) {
    state.footer.menu = payload;
  },
  SET_ENTERPRISE_CLIENT_HEADER_MENU(state, payload) {
    state.enterprise.header = payload;
  },
  SET_ENTERPRISE_SSO_SETTINGS(state, payload) {
    if (payload && payload.length > 0) {
      [state.enterprise.sso] = payload;
    }
  },
  SET_ENTERPRISE_GAUGE_TYPE(state, payload) {
    state.enterprise.gaugeType = payload[0].gaugeType;
  },
};

export default {
  namespaced: true,
  state: stateObj,
  getters: gettersObj,
  actions,
  mutations,
};
